import posthog from 'posthog-js';
import config from '@/config/config.env';

/**
 * Initialize PostHog analytics.
 *
 * Industry-standard performance optimization (used by Linear, Vercel, Notion):
 *   - `api_transport: 'sendBeacon'` → analytics traffic uses the browser's Beacon API
 *     (fire-and-forget on a separate thread) instead of XHR, so it NEVER competes
 *     with your API calls for the 6 HTTP/1.1 connection slots. This directly fixes
 *     the 25s browser queueing seen in the network waterfall.
 *   - `request_batching: true` → batches multiple events into a single request
 *   - `batch_size / flush_interval` → controls how aggressively events are batched
 *   - Initialization is deferred with requestIdleCallback so the browser completes
 *     the critical page render before PostHog runs.
 */
export function initPosthog() {
  const apiKey = config.posthog?.apiKey;
  const apiHost = config.posthog?.apiHost || 'https://us.i.posthog.com';

  if (!apiKey) {
    console.info('[PostHog] Project key missing. Telemetry is in simulation mode.');
    return;
  }

  // Defer PostHog initialization to browser idle time.
  // This prevents analytics scripts from competing with critical API requests.
  const init = () => {
    posthog.init(apiKey, {
      api_host: apiHost,

      // ── Critical: use Beacon instead of XHR ──
      // Beacon sends requests asynchronously via the OS-level network stack,
      // freeing up all 6 HTTP/1.1 browser connection slots for your API calls.
      api_transport: 'sendBeacon',

      // Batch analytics events — reduces total requests from N to ceil(N/batch_size)
      request_batching: true,
      batch_size: 30,          // Send when 30 events accumulate
      flush_interval: 5000,    // Or every 5 seconds, whichever comes first

      loaded: (ph) => {
        if (config.environment === 'development') {
          ph.debug(true);
        }
      },
      capture_pageview: false,   // Managed manually via router hooks
      persistence: 'cookie',
      capture_exceptions: true,  // PostHog built-in Error Tracking

      // Disable session replay in dev to reduce noise
      disable_session_recording: config.environment === 'development',
    });
  };

  // requestIdleCallback → only runs when the browser is not busy painting/scripting.
  // Falls back to a 3s timeout on Safari (which doesn't support rIC).
  if (typeof window !== 'undefined') {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(init, { timeout: 3000 });
    } else {
      setTimeout(init, 2000); // Safari fallback: 2s after page load
    }
  }
}

export { posthog };
